import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import UnifiedHeader from '@/components/common/UnifiedHeader';
import LogoutConfirmDialog from '@/components/common/LogoutConfirmDialog';
import PatientEnrollmentForm from '@/components/es/PatientEnrollmentForm';
import GlobalSearch from '@/components/features/search/GlobalSearch';
import { useToast } from '@/hooks/use-toast';
import { useSMSCount } from '@/hooks/sms/useSMSCount';
import { useVoicemailCount } from '@/hooks/voicemail/useVoicemailCount';

interface ESDashboardProps {
  onPatientSelect?: (patientId: number, clinicId?: number) => void;
}

const ESDashboard = ({ onPatientSelect }: ESDashboardProps = {}) => {
  const { logout, user, token, hasuraToken } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [currentPatientId, setCurrentPatientId] = useState<number | null>(null);
  const [externalPatientId, setExternalPatientId] = useState<number | null>(null);
  const [externalActivityId, setExternalActivityId] = useState<number | null>(null);
  const [externalWorkflowId, setExternalWorkflowId] = useState<number | null>(null);
  const [fromExternalSource, setFromExternalSource] = useState(false);
  const [returnRoute, setReturnRoute] = useState<string>('');
  const [isFromGlobalSearch, setIsFromGlobalSearch] = useState(false);
  
  // Fetch SMS and voicemail counts with refetch methods
  const { refetch: refetchSMSCount } = useSMSCount();
  const { refetch: refetchVoicemailCount } = useVoicemailCount();
  
  // Create ref to pass refetch methods to PatientEnrollmentForm
  const headerRefetchRef = useRef({
    refetchSMSCount,
    refetchVoicemailCount
  });

  // Check for patientId from URL params or location state (from PE/CS dashboards or Global Search)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const fromParam = searchParams.get('from');
    const patientIdParam = searchParams.get('patientId');
    const activityIdParam = searchParams.get('activityId');
    const workflowIdParam = searchParams.get('workflowId');
    
    // Check URL params first (persists on refresh), then fall back to location state
    if (fromParam && patientIdParam) {
      setExternalPatientId(parseInt(patientIdParam, 10));
      setExternalActivityId(activityIdParam ? parseInt(activityIdParam, 10) : null);
      setExternalWorkflowId(workflowIdParam ? parseInt(workflowIdParam, 10) : null);
      setFromExternalSource(true);
      
      // Set return route based on source
      if (fromParam === 'cs-dashboard') {
        setReturnRoute('/dashboard');
        setIsFromGlobalSearch(false);
      } else if (fromParam === 'pe-dashboard') {
        setReturnRoute('/dashboard');
        setIsFromGlobalSearch(false);
      } else if (fromParam === 'global-search') {
        setReturnRoute('/global-search');
        setIsFromGlobalSearch(true);
      } else if (fromParam === 'transfer-patients') {
        setReturnRoute('/transfer-patients');
        setIsFromGlobalSearch(false);
      } else if (fromParam === 'incoming-call') {
        setReturnRoute('/dashboard');
        setIsFromGlobalSearch(false);
      }
    } else if (location.state?.patientId) {
      setExternalPatientId(location.state.patientId);
      setExternalActivityId(location.state.activityId || null);
      setExternalWorkflowId(location.state.workflowId || null);
      setFromExternalSource(true);
      // Clear the state to prevent reloading on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.search, location.state, location.pathname, navigate]);

  const handleBackToSource = () => {
    setExternalPatientId(null);
    setExternalActivityId(null);
    setExternalWorkflowId(null);
    setFromExternalSource(false);
    
    // Navigate to the return route if set, otherwise go back
    if (returnRoute) {
      navigate(returnRoute);
    } else {
      navigate(-1);
    }
  };

  const handleLogout = async () => {
    // Logout function now handles unlock for ES users in AuthContext
    await logout();
    setShowLogoutDialog(false);
    toast({
      title: "Signed Out",
      description: "You have been successfully signed out.",
    });
  };

  const handleLogoutRequest = (patientId?: number) => {
    console.log('Logout requested with patient ID:', patientId);
    setCurrentPatientId(patientId || null);
    setShowLogoutDialog(true);
  };

  const handleSearchPatientsClick = () => {
    setShowGlobalSearch(true);
  };

  const handleBackToMain = () => {
    setShowGlobalSearch(false);
  };

  if (showGlobalSearch) {
    return <GlobalSearch onBack={handleBackToMain} data-testid="es-global-search" />;
  }

  // PatientEnrollmentForm will handle all patient data logic and empty states internally
   return (
    <div className="min-h-screen " data-testid="es-dashboard">
      <div className="relative" data-testid="es-dashboard-content">
        <PatientEnrollmentForm 
          onLogout={handleLogoutRequest} 
          headerRefetchMethods={headerRefetchRef.current}
          externalPatientId={externalPatientId}
          externalActivityId={externalActivityId}
          externalWorkflowId={externalWorkflowId}
          onPatientLoaded={() => setExternalPatientId(null)}
          showBackButton={fromExternalSource}
          onBack={handleBackToSource}
          isFromGlobalSearch={isFromGlobalSearch}
          data-testid="es-patient-enrollment-form" 
        />
      </div>
      <LogoutConfirmDialog
        open={showLogoutDialog}
        onOpenChange={setShowLogoutDialog}
        onConfirm={handleLogout}
        data-testid="es-logout-confirm-dialog"
      />
    </div>
  );
};

export default ESDashboard;